import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const Footer = () => {
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'en' ? 'fa' : 'en';
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
    // In a real app, this would trigger a page refresh or context update
    window.location?.reload();
  };

  const content = {
    en: {
      company: {
        name: 'Slotyfi',
        tagline:
          'Transforming booking chaos into seamless customer experiences',
        description:
          'The AI-powered booking automation platform that helps businesses reduce no-shows, save time, and increase revenue through intelligent appointment management.',
      },
      links: {
        product: {
          title: 'Product',
          items: [
            { name: 'Features', href: '#features' },
            { name: 'Pricing', href: '#pricing' },
            { name: 'Integrations', href: '/integrations' },
            { name: 'API Documentation', href: '/docs' },
            { name: 'Changelog', href: '/changelog' },
          ],
        },
        company: {
          title: 'Company',
          items: [
            { name: 'About Us', href: '/about' },
            { name: 'Careers', href: '/careers' },
            { name: 'Press Kit', href: '/press' },
            { name: 'Contact', href: '/contact' },
            { name: 'Blog', href: '/blog' },
          ],
        },
        support: {
          title: 'Support',
          items: [
            { name: 'Help Center', href: '/help' },
            { name: 'Live Chat', href: '#chat' },
            { name: 'Status Page', href: '/status' },
            { name: 'Community', href: '/community' },
            { name: 'Training', href: '/training' },
          ],
        },
        legal: {
          title: 'Legal',
          items: [
            { name: 'Privacy Policy', href: '/privacy' },
            { name: 'Terms of Service', href: '/terms' },
            { name: 'Cookie Policy', href: '/cookies' },
            { name: 'GDPR', href: '/gdpr' },
            { name: 'Security', href: '/security' },
          ],
        },
      },
      social: {
        title: 'Follow Us',
        platforms: [
          {
            name: 'Twitter',
            icon: 'Twitter',
            href: 'https://twitter.com/slotyfi',
          },
          {
            name: 'LinkedIn',
            icon: 'Linkedin',
            href: 'https://linkedin.com/company/slotyfi',
          },
          {
            name: 'Facebook',
            icon: 'Facebook',
            href: 'https://facebook.com/slotyfi',
          },
          {
            name: 'Instagram',
            icon: 'Instagram',
            href: 'https://instagram.com/slotyfi',
          },
          {
            name: 'YouTube',
            icon: 'Youtube',
            href: 'https://youtube.com/slotyfi',
          },
        ],
      },
      newsletter: {
        title: 'Stay Updated',
        description:
          'Get the latest updates, tips, and insights delivered to your inbox.',
        placeholder: 'Enter your email',
        button: 'Subscribe',
        privacy: 'We respect your privacy. Unsubscribe anytime.',
      },
      bottom: {
        copyright: '© 2025 Slotyfi. All rights reserved.',
        language: 'Language',
        madeWith: 'Made with ❤️ for businesses worldwide',
      },
    },
    fa: {
      company: {
        name: 'اسلاتیفای',
        tagline: 'تبدیل فرآیندهای آشفته رزرو به تجربه‌ای روان و بی‌دردسر',
        description:
          'پلتفرم خودکارسازی رزرو مبتنی بر هوش مصنوعی که به کسب‌وکارها کمک می‌کند غیبت‌ها را کاهش دهند، زمان صرفه‌جویی کنند و از طریق مدیریت هوشمند قرارملاقات درآمد افزایش یابد.',
      },
      links: {
        product: {
          title: 'محصول',
          items: [
            { name: 'ویژگی‌ها', href: '#features' },
            { name: 'قیمت‌گذاری', href: '#pricing' },
            { name: 'یکپارچگی‌ها', href: '/integrations' },
            { name: 'مستندات API', href: '/docs' },
            { name: 'تغییرات', href: '/changelog' },
          ],
        },
        company: {
          title: 'شرکت',
          items: [
            { name: 'درباره ما', href: '/about' },
            { name: 'فرصت‌های شغلی', href: '/careers' },
            { name: 'کیت مطبوعاتی', href: '/press' },
            { name: 'تماس', href: '/contact' },
            { name: 'وبلاگ', href: '/blog' },
          ],
        },
        support: {
          title: 'پشتیبانی',
          items: [
            { name: 'مرکز راهنمایی', href: '/help' },
            { name: 'چت زنده', href: '#chat' },
            { name: 'صفحه وضعیت', href: '/status' },
            { name: 'جامعه', href: '/community' },
            { name: 'آموزش', href: '/training' },
          ],
        },
        legal: {
          title: 'قانونی',
          items: [
            { name: 'سیاست حریم خصوصی', href: '/privacy' },
            { name: 'شرایط خدمات', href: '/terms' },
            { name: 'سیاست کوکی', href: '/cookies' },
            { name: 'GDPR', href: '/gdpr' },
            { name: 'امنیت', href: '/security' },
          ],
        },
      },
      social: {
        title: 'ما را دنبال کنید',
        platforms: [
          {
            name: 'توییتر',
            icon: 'Twitter',
            href: 'https://twitter.com/slotyfi',
          },
          {
            name: 'لینکدین',
            icon: 'Linkedin',
            href: 'https://linkedin.com/company/slotyfi',
          },
          {
            name: 'فیسبوک',
            icon: 'Facebook',
            href: 'https://facebook.com/slotyfi',
          },
          {
            name: 'اینستاگرام',
            icon: 'Instagram',
            href: 'https://instagram.com/slotyfi',
          },
          {
            name: 'یوتیوب',
            icon: 'Youtube',
            href: 'https://youtube.com/slotyfi',
          },
        ],
      },
      newsletter: {
        title: 'به‌روز بمانید',
        description:
          'آخرین به‌روزرسانی‌ها، نکات و بینش‌ها را در صندوق ورودی خود دریافت کنید.',
        placeholder: 'ایمیل خود را وارد کنید',
        button: 'عضویت',
        privacy:
          'ما به حریم خصوصی شما احترام می‌گذاریم. هر زمان لغو عضویت کنید.',
      },
      bottom: {
        copyright: '© 2025 اسلاتیفای. تمام حقوق محفوظ است.',
        language: 'زبان',
        madeWith: 'با ❤️ برای کسب‌وکارهای سراسر جهان ساخته شده',
      },
    },
  };

  const currentContent = content?.[language];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-6 py-16">
        <div
          className={`grid lg:grid-cols-6 gap-12 ${
            language === 'fa' ? 'rtl' : 'ltr'
          }`}>
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                <Icon name="Calendar" size={24} color="white" />
              </div>
              <h3 className="text-2xl font-bold">
                {currentContent?.company?.name}
              </h3>
            </div>

            <p className="text-blue-400 font-medium mb-4">
              {currentContent?.company?.tagline}
            </p>

            <p className="text-gray-400 leading-relaxed mb-6">
              {currentContent?.company?.description}
            </p>

            {/* Social Links */}
            <div>
              <h4 className="font-semibold mb-4">
                {currentContent?.social?.title}
              </h4>
              <div className="flex space-x-4">
                {currentContent?.social?.platforms?.map((platform, index) => (
                  <a
                    key={index}
                    href={platform?.href}
                    className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors duration-300"
                    target="_blank"
                    rel="noopener noreferrer">
                    <Icon name={platform?.icon} size={20} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Links Sections */}
          <div className="lg:col-span-3 grid md:grid-cols-4 gap-8">
            {Object.entries(currentContent?.links)?.map(([key, section]) => (
              <div key={key}>
                <h4 className="font-semibold mb-4">{section?.title}</h4>
                <ul className="space-y-3">
                  {section?.items?.map((item, index) => (
                    <li key={index}>
                      <a
                        href={item?.href}
                        className="text-gray-400 hover:text-white transition-colors duration-300">
                        {item?.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Newsletter */}
          <div className="lg:col-span-1">
            <h4 className="font-semibold mb-4">
              {currentContent?.newsletter?.title}
            </h4>
            <p className="text-gray-400 text-sm mb-6">
              {currentContent?.newsletter?.description}
            </p>

            <div className="space-y-4">
              <div className="flex">
                <input
                  type="email"
                  placeholder={currentContent?.newsletter?.placeholder}
                  className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-l-lg focus:outline-none focus:border-blue-500 text-white"
                />
                <Button
                  variant="default"
                  className="bg-blue-600 hover:bg-blue-700 rounded-l-none"
                  iconName="Send">
                  {currentContent?.newsletter?.button}
                </Button>
              </div>

              <p className="text-xs text-gray-500">
                {currentContent?.newsletter?.privacy}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-6 py-6">
          <div
            className={`flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 ${
              language === 'fa' ? 'rtl' : 'ltr'
            }`}>
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <p className="text-gray-400 text-sm">
                {currentContent?.bottom?.copyright}
              </p>
              <p className="text-gray-500 text-sm">
                {currentContent?.bottom?.madeWith}
              </p>
            </div>

            {/* Language Switcher */}
            <div className="flex items-center space-x-4">
              <span className="text-gray-400 text-sm">
                {currentContent?.bottom?.language}:
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="text-gray-400 hover:text-white"
                iconName="Globe"
                iconPosition="left">
                {language === 'en' ? 'فارسی' : 'English'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Trust Badges */}
      <div className="border-t border-gray-800 bg-gray-950">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-wrap justify-center items-center space-x-8 opacity-60">
            <div className="flex items-center space-x-2 mb-4">
              <Icon name="Shield" size={20} color="#10B981" />
              <span className="text-sm text-gray-400">SOC 2 Certified</span>
            </div>
            <div className="flex items-center space-x-2 mb-4">
              <Icon name="Lock" size={20} color="#10B981" />
              <span className="text-sm text-gray-400">GDPR Compliant</span>
            </div>
            <div className="flex items-center space-x-2 mb-4">
              <Icon name="CheckCircle" size={20} color="#10B981" />
              <span className="text-sm text-gray-400">99.9% Uptime</span>
            </div>
            <div className="flex items-center space-x-2 mb-4">
              <Icon name="Award" size={20} color="#10B981" />
              <span className="text-sm text-gray-400">ISO 27001</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
